import React, { useState, useEffect } from 'react';
import { supabase } from '../../services/supabaseClient';
import { getCurrentUser } from '../../services/authService';

const Settings = () => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');

  const [profileForm, setProfileForm] = useState({
    full_name: '',
    bio: '',
    type: '',
    instruments: '',
    rate: '',
    location: '',
    capacity: '',
    avatar_url: ''
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const fetchUserAndProfile = async () => {
      try {
        setLoading(true);

        const { user: userData } = await getCurrentUser();
        if (!userData) {
          throw new Error('User not authenticated');
        }

        setUser(userData);

        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('uuid', userData.id)
          .single();

        if (profileError) throw profileError;

        setProfile(profileData);

        setProfileForm({
          full_name: profileData.full_name || '',
          bio: profileData.bio || '',
          type: profileData.type || '',
          instruments: profileData.instruments || '',
          rate: profileData.rate || '',
          location: profileData.location || '',
          capacity: profileData.capacity || '',
          avatar_url: profileData.avatar_url || ''
        });

        setError(null);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load user profile. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserAndProfile();
  }, []);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      if (!user) {
        throw new Error('User not authenticated');
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          full_name: profileForm.full_name,
          bio: profileForm.bio,
          type: profileForm.type,
          instruments: profileForm.instruments,
          rate: profileForm.rate,
          location: profileForm.location,
          capacity: profileForm.capacity,
          updated_at: new Date()
        })
        .eq('uuid', user.id);

      if (updateError) throw updateError;

      setProfile(prev => ({
        ...prev,
        ...profileForm
      }));

      setSuccess('Profile updated successfully');
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        throw new Error('New passwords do not match');
      }

      if (passwordForm.newPassword.length < 6) {
        throw new Error('Password must be at least 6 characters');
      }

      const { error: passwordError } = await supabase.auth.updateUser({
        password: passwordForm.newPassword
      });

      if (passwordError) throw passwordError;

      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      setSuccess('Password updated successfully');
    } catch (err) {
      console.error('Error updating password:', err);
      setError(err.message || 'Failed to update password. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    try {
      setSaving(true);
      setError(null);

      if (!e.target.files || e.target.files.length === 0) {
        return;
      }

      const file = e.target.files[0];
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('profiles')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('profiles')
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('uuid', user.id);

      if (updateError) throw updateError;

      setProfileForm(prev => ({
        ...prev,
        avatar_url: publicUrl
      }));

      setProfile(prev => ({
        ...prev,
        avatar_url: publicUrl
      }));

      setSuccess('Profile picture updated successfully');
    } catch (err) {
      console.error('Error uploading avatar:', err);
      setError('Failed to upload profile picture. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container py-5">
      <h1 className="mb-4">Account Settings</h1>

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccess(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error && !profile ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <div className="row mt-4">
          <div className="col-md-3">
            <div className="list-group">
              <button
                className={`list-group-item list-group-item-action ${activeTab === 'profile' ? 'active' : ''}`}
                onClick={() => setActiveTab('profile')}
              >
                Profile Information
              </button>
              <button
                className={`list-group-item list-group-item-action ${activeTab === 'password' ? 'active' : ''}`}
                onClick={() => setActiveTab('password')}
              >
                Change Password
              </button>
            </div>
          </div>

          <div className="col-md-9">
            {activeTab === 'profile' && (
              <div className="card">
                <div className="card-header">Profile Information</div>
                <div className="card-body">
                  <div className="d-flex align-items-center mb-3">
                    {profileForm.avatar_url ? (
                      <img
                        src={profileForm.avatar_url}
                        alt="Profile"
                        className="rounded-circle me-3"
                        width="100"
                        height="100"
                      />
                    ) : (
                      <div
                        className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center me-3"
                        style={{ width: '100px', height: '100px', fontSize: '2rem' }}
                      >
                        {profileForm.full_name.charAt(0) || user?.email.charAt(0)}
                      </div>
                    )}
                    <div>
                      <input
                        type="file"
                        className="form-control"
                        id="avatar"
                        accept="image/*"
                        onChange={handleAvatarUpload}
                      />
                      <div className="form-text">
                        Upload a profile picture (max 5MB)
                      </div>
                    </div>
                  </div>

                  <form onSubmit={handleProfileSubmit}>
                    <div className="mb-3">
                      <label htmlFor="full_name" className="form-label">Full Name</label>
                      <input
                        type="text"
                        className="form-control"
                        id="full_name"
                        name="full_name"
                        value={profileForm.full_name}
                        onChange={handleProfileChange}
                      />
                    </div>

                    <div className="mb-3">
                      <label htmlFor="bio" className="form-label">Bio</label>
                      <textarea
                        className="form-control"
                        id="bio"
                        name="bio"
                        rows="3"
                        value={profileForm.bio}
                        onChange={handleProfileChange}
                      ></textarea>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="type" className="form-label">Profile Type</label>
                      <select
                        className="form-select"
                        id="type"
                        name="type"
                        value={profileForm.type}
                        onChange={handleProfileChange}
                      >
                        <option value="">Select Type</option>
                        <option value="artist">Artist</option>
                        <option value="venue">Venue</option>
                      </select>
                    </div>

                    {profileForm.type === 'artist' && (
                      <>
                        <div className="mb-3">
                          <label htmlFor="instruments" className="form-label">Instruments</label>
                          <input
                            type="text"
                            className="form-control"
                            id="instruments"
                            name="instruments"
                            value={profileForm.instruments}
                            onChange={handleProfileChange}
                            placeholder="e.g. Guitar, Piano, Vocals"
                          />
                          <div className="form-text">
                            Separate multiple instruments with commas
                          </div>
                        </div>

                        <div className="mb-3">
                          <label htmlFor="rate" className="form-label">Hourly Rate ($)</label>
                          <input
                            type="number"
                            className="form-control"
                            id="rate"
                            name="rate"
                            value={profileForm.rate}
                            onChange={handleProfileChange}
                            min="0"
                            step="0.01"
                          />
                        </div>
                      </>
                    )}

                    {profileForm.type === 'venue' && (
                      <>
                        <div className="mb-3">
                          <label htmlFor="location" className="form-label">Location</label>
                          <input
                            type="text"
                            className="form-control"
                            id="location"
                            name="location"
                            value={profileForm.location}
                            onChange={handleProfileChange}
                          />
                        </div>

                        <div className="mb-3">
                          <label htmlFor="capacity" className="form-label">Venue Capacity</label>
                          <input
                            type="number"
                            className="form-control"
                            id="capacity"
                            name="capacity"
                            value={profileForm.capacity}
                            onChange={handleProfileChange}
                            min="0"
                          />
                        </div>
                      </>
                    )}

                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Profile'}
                    </button>
                  </form>
                </div>
              </div>
            )}

            {activeTab === 'password' && (
              <div className="card">
                <div className="card-body">
                  <h5 className="card-title">Change Password</h5>

                  <form onSubmit={handlePasswordSubmit}>
                    <div className="mb-3">
                      <label htmlFor="currentPassword" className="form-label">Current Password</label>
                      <input
                        type="password"
                        className="form-control"
                        id="currentPassword"
                        name="currentPassword"
                        value={passwordForm.currentPassword}
                        onChange={handlePasswordChange}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label htmlFor="newPassword" className="form-label">New Password</label>
                      <input
                        type="password"
                        className="form-control"
                        id="newPassword"
                        name="newPassword"
                        value={passwordForm.newPassword}
                        onChange={handlePasswordChange}
                        required
                        minLength="6"
                      />
                      <div className="form-text">
                        Password must be at least 6 characters
                      </div>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="confirmPassword" className="form-label">Confirm New Password</label>
                      <input
                        type="password"
                        className="form-control"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={passwordForm.confirmPassword}
                        onChange={handlePasswordChange}
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving}
                    >
                      {saving ? 'Updating...' : 'Update Password'}
                    </button>
                  </form>

                  <hr className="my-4" />

                  <div className="mt-4">
                    <h5 className="text-danger">Danger Zone</h5>
                    <p>Once you delete your account, there is no going back. Please be certain.</p>
                    <button
                      className="btn btn-outline-danger"
                      onClick={() => alert('Account deletion is disabled in this demo')}
                    >
                      Delete Account
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
